import React, { useState, useEffect, useMemo } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Analytics01FreeIcons } from '@hugeicons/core-free-icons';
import { FavouriteIcon, BalanceScaleIcon, Notification01Icon, Share01Icon } from 'hugeicons-react';
import { supabase } from '../lib/supabase';
import { CATS, TC, BADGES, BADGE_CAT_LABELS } from '../data/constants';
import { ReviewForm } from './ReviewForm';
import { Maskot } from './Maskot';
import { getMaskotForCategory } from '../utils/maskot';
import { matchScore, scoreColor } from '../utils/matching';
// imgUrl helper kaldırıldı — Supabase render endpoint Google Places photos'la
// uyumsuz çıktı, render/image/public yolu Pro plan gerektirebilir veya
// content-type sorun çıkarıyor. Direkt orijinal URL kullanılıyor.
// onImgErr: render endpoint'e (eğer ileride yeniden eklenirse) düşürse
// orijinaline fallback için emniyet katmanı.
const onImgErr = (e) => {
  const el = e.currentTarget;
  if (el.dataset.fallback) return;
  el.dataset.fallback = '1';
  // src render endpoint formatındaysa object public'e çevir
  if (el.src.includes('/storage/v1/render/image/public/')) {
    el.src = el.src.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/').split('?')[0];
  }
};

// Plan order: yüksek = daha çok özellik
// Yeni model (2026-05-04): ucretsiz < premium < okulbot < kurumsal
const PLAN_RANK = { ucretsiz: 1, premium: 2, okulbot: 3, kurumsal: 4 };
function planAtLeast(current, target) {
  return (PLAN_RANK[current] || 1) >= (PLAN_RANK[target] || 1);
}

export const DetailModal = React.memo(function DetailModal({
  place, theme, user, favs, compareList, schoolBadges,
  onClose, onOpenInstitution, onBilgiAl, onClaim, onShare, onAlert,
  toggleFav, handleCompare, onShowAuth, showToast, trackAnalytics,
  onWalkRoute, onClearWalkRoute, walkRoute,
  userPrefs, onOpenPrefs,
}) {
  const C = (k) => {
    const d = theme === 'dark';
    const m = {bg:d?'#0d1117':'#FFF8F0',surf:d?'#141924':'#FFFAF5',surf2:d?'rgba(255,255,255,0.03)':'rgba(139,92,246,0.04)',bord:d?'rgba(255,255,255,0.07)':'rgba(139,92,246,0.10)',bord2:d?'rgba(255,255,255,0.12)':'rgba(139,92,246,0.14)',txt:d?'#ffffff':'#1E1048',txt2:d?'rgba(255,255,255,0.5)':'#4B3D7A',txt3:d?'rgba(255,255,255,0.25)':'#9B8FBD',inp:d?'rgba(255,255,255,0.05)':'rgba(139,92,246,0.06)'};
    return m[k]||'';
  };
  const p = place;
  const color = p.sponsored ? '#FFD700' : TC[p.type];
  const cat = CATS.find(c=>c.id===p.type)||{};
  const fillPct = Math.round((p.capacity > 0 ? p.filled/p.capacity : 0)*100);
  const fillColor = fillPct>80?'#FF6B6B':fillPct>60?'#FFE66D':'#4ECDC4';
  const isFav = favs.includes(p.id);
  const inCmp = compareList.includes(p.id);

  const [tab, setTab] = useState('genel');
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [analytics, setAnalytics] = useState({ views: 0, favs: 0, apps: 0 });
  const [schoolPlan, setSchoolPlan] = useState('ucretsiz');
  const memoPlace = useMemo(() => place, [place?.id]);

  // Okul planını çek — WhatsApp butonu gibi plan-bazlı özellikler için
  useEffect(() => {
    if (!p?.id) return;
    let cancelled = false;
    supabase.from('school_subscriptions')
      .select('plan, end_date, status')
      .eq('school_id', p.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (!data) { setSchoolPlan('ucretsiz'); return; }
        // end_date geçmişse plan ücretsiz'e düşer
        const expired = data.end_date && new Date(data.end_date) < new Date();
        if (expired || data.status === 'expired' || data.status === 'cancelled') {
          setSchoolPlan('ucretsiz');
        } else {
          setSchoolPlan(data.plan || 'ucretsiz');
        }
      })
      .catch(() => setSchoolPlan('ucretsiz'));
    return () => { cancelled = true; };
  }, [p?.id]);

  useEffect(() => {
    if (!p?.id) return;
    Promise.all([
      supabase.from('school_analytics').select('event_type').eq('school_id', p.id),
      supabase.from('enrollments').select('id', { count: 'exact', head: true }).eq('school_id', p.id),
    ]).then(([analyticsRes, enrollRes]) => {
      const rows = analyticsRes.data || [];
      setAnalytics({
        views: rows.filter(r => r.event_type === 'view').length,
        favs:  rows.filter(r => r.event_type === 'favorite').length,
        apps:  enrollRes.count || 0,
      });
    });
  }, [p?.id]);

  const hasRealPhotos = p.photos && p.photos.length > 0 && typeof p.photos[0] === 'string' && p.photos[0].startsWith('http');
  const PHOTOS = hasRealPhotos
    ? p.photos.map((url, i) => ({ url, label: `Fotoğraf ${i+1}`, grad: '135deg,#0d111780,#0d111740' }))
    : [
      {emoji:'🏫', label:'Giriş', grad:'135deg,'+color+'40,'+color+'15'},
      {emoji:'🌿', label:'Bahçe', grad:'135deg,#4ECDC420,#4ECDC408'},
      {emoji:'📚', label:'Sınıf', grad:'135deg,#A8DAFF20,#A8DAFF08'},
    ];

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % PHOTOS.length);
      if (e.key === 'ArrowLeft') setLightboxIdx(i => (i - 1 + PHOTOS.length) % PHOTOS.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, PHOTOS.length]);

  const handleTabClick = (id) => {
    setTab(id);
    if (id === 'yorumlar' && reviews.length === 0) {
      setReviewsLoading(true);
      supabase.from('school_reviews').select('*').eq('school_id',p.id).eq('is_approved',true).order('created_at',{ascending:false}).limit(20)
        .then(res=>{ setReviews(res.data||[]); setReviewsLoading(false); });
    }
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:800,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.7)',backdropFilter:'blur(6px)'}}
      onClick={onClose}>
      <div style={{background:C('surf'),borderRadius:20,width:480,height:'82vh',display:'flex',flexDirection:'column',animation:'fadeIn 0.2s ease',boxShadow:'0 24px 64px rgba(0,0,0,0.5)',overflow:'hidden'}}
        onClick={e=>e.stopPropagation()}>

        {/* Photo carousel hero */}
        <div style={{position:'relative',height:180,flexShrink:0,overflow:'hidden',background:'linear-gradient('+PHOTOS[photoIdx].grad+')'}}>
          {PHOTOS[photoIdx].url
            ? <img src={PHOTOS[photoIdx].url} alt={PHOTOS[photoIdx].label} loading="lazy" decoding="async" onError={onImgErr} style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />
            : <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:72}}>
                <span style={{filter:'drop-shadow(0 8px 24px rgba(0,0,0,0.4))'}}>{PHOTOS[photoIdx].emoji}</span>
              </div>
          }
          <div style={{position:'absolute',bottom:10,left:'50%',transform:'translateX(-50%)',display:'flex',gap:5,zIndex:3}}>
            {PHOTOS.map((ph,i)=>(
              <div key={i} onClick={()=>setPhotoIdx(i)}
                style={{width:i===photoIdx?18:6,height:6,borderRadius:3,background:i===photoIdx?'#fff':'rgba(255,255,255,0.35)',cursor:'pointer',transition:'all 0.2s'}}/>
            ))}
          </div>
          <div style={{position:'absolute',bottom:24,right:12,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(6px)',borderRadius:6,padding:'2px 8px',fontSize:10,color:'rgba(255,255,255,0.7)',fontFamily:'DM Sans,sans-serif'}}>
            {PHOTOS[photoIdx].label}
          </div>
          <button onClick={()=>setPhotoIdx(i=>(i-1+PHOTOS.length)%PHOTOS.length)}
            style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',width:28,height:28,borderRadius:8,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(6px)',border:'1px solid rgba(255,255,255,0.1)',color:'#fff',fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:3}}>‹</button>
          <button onClick={()=>setPhotoIdx(i=>(i+1)%PHOTOS.length)}
            style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',width:28,height:28,borderRadius:8,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(6px)',border:'1px solid rgba(255,255,255,0.1)',color:'#fff',fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:3}}>›</button>
          <button onClick={onClose}
            style={{position:'absolute',top:12,left:12,width:34,height:34,borderRadius:10,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.1)',color:'#fff',fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:3}}>←</button>
          <div style={{position:'absolute',top:12,right:12,display:'flex',gap:6,zIndex:3}}>
            <button onClick={()=>toggleFav(p.id)} style={{width:34,height:34,borderRadius:10,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
              <FavouriteIcon size={16} color={isFav?'#FF6B6B':'rgba(255,255,255,0.85)'} />
            </button>
            <button onClick={()=>handleCompare(p.id)} style={{width:34,height:34,borderRadius:10,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
              <BalanceScaleIcon size={16} color={inCmp?'#A8DAFF':'rgba(255,255,255,0.85)'} />
            </button>
            <button onClick={onAlert} style={{width:34,height:34,borderRadius:10,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
              <Notification01Icon size={16} color='rgba(255,255,255,0.85)' />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',borderBottom:'1px solid '+C('bord'),flexShrink:0,background:C('surf')}}>
          {[{id:'genel',label:'Genel',icon:'🏠'},{id:'fotolar',label:'Fotoğraflar',icon:'🖼'},{id:'yorumlar',label:'Yorumlar',icon:'💬'},{id:'istatistik',label:'İstatistikler',icon:null}].map(t=>(
            <button key={t.id} onClick={()=>handleTabClick(t.id)}
              style={{flex:1,padding:'10px 4px',background:'none',border:'none',borderBottom:'2px solid '+(tab===t.id?color:'transparent'),color:tab===t.id?color:C('txt2'),fontSize:12,fontWeight:tab===t.id?700:400,cursor:'pointer',fontFamily:'DM Sans,sans-serif',display:'flex',alignItems:'center',justifyContent:'center',gap:4,transition:'all 0.15s'}}>
              {t.icon
                ? <span>{t.icon}</span>
                : <HugeiconsIcon icon={Analytics01FreeIcons} size={14} color={tab===t.id?color:'rgba(255,255,255,0.4)'} />
              }
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div style={{flex:1,overflowY:'auto',padding:'16px 18px 4px',minHeight:0}}>
          {tab==='istatistik'&&(
            <div style={{paddingBottom:8}}>
              <div style={{fontSize:10,fontWeight:700,color:C('txt3'),letterSpacing:'0.08em',marginBottom:10}}>BU AY</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
                {[
                  {icon:'👁',  val:analytics.views, label:'Profil Görüntüleme', col:'#A8DAFF'},
                  {icon:'❤️', val:analytics.favs,  label:'Favoriye Ekleme',    col:'#FF6B6B'},
                  {icon:'📋', val:analytics.apps,  label:'Bilgi Talebi',       col:'#4ECDC4'},
                  {icon:'👥', val:p.filled+'/'+p.capacity, label:'Kapasite',   col:fillColor},
                ].map((item,i)=>(
                  <div key={i} style={{background:C('surf2'),borderRadius:12,padding:14,border:'1px solid '+C('bord'),textAlign:'center'}}>
                    <div style={{fontSize:22,marginBottom:4}}>{item.icon}</div>
                    <div style={{color:item.col,fontSize:20,fontWeight:800,fontFamily:'Syne,sans-serif',lineHeight:1}}>{item.val}</div>
                    <div style={{color:C('txt2'),fontSize:11,marginTop:4}}>{item.label}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:10,fontWeight:700,color:C('txt3'),letterSpacing:'0.08em',marginBottom:10}}>DOLULUK</div>
              <div style={{background:C('surf2'),borderRadius:12,padding:14,border:'1px solid '+C('bord'),marginBottom:20}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,fontSize:12}}>
                  <span style={{color:C('txt2')}}>Kayıtlı / Kapasite</span>
                  <span style={{color:fillColor,fontWeight:700}}>{p.filled} / {p.capacity} — %{fillPct}</span>
                </div>
                <div style={{height:8,background:'rgba(255,255,255,0.06)',borderRadius:4,overflow:'hidden'}}>
                  <div style={{height:'100%',width:fillPct+'%',background:fillColor,borderRadius:4,transition:'width 0.8s'}}/>
                </div>
                {fillPct>85 && <div style={{color:'#FF6B6B',fontSize:11,marginTop:8}}>⚠ Kontenjan dolmak üzere</div>}
              </div>
              {/* Yıldız Puanlama */}
              <div style={{background:C('surf2'),borderRadius:12,padding:'12px 14px',border:'1px solid '+C('bord'),display:'flex',alignItems:'center',gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:10,fontWeight:700,color:C('txt3'),letterSpacing:'0.08em',marginBottom:6}}>DEĞERLENDİRME</div>
                  <div style={{display:'flex',alignItems:'center',gap:2,marginBottom:4}}>
                    {[1,2,3,4,5].map(i=>(
                      <span key={i} style={{fontSize:20,color:i<=Math.round(p.rating)?'#FFE66D':'rgba(255,255,255,0.12)',lineHeight:1}}>
                        {i<=Math.round(p.rating)?'★':'☆'}
                      </span>
                    ))}
                  </div>
                  <div style={{color:C('txt2'),fontSize:11}}>({p.reviews} yorum)</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#FFE66D',fontSize:30,fontWeight:800,lineHeight:1,fontFamily:'Syne,sans-serif'}}>{p.rating}</div>
                  <div style={{color:C('txt3'),fontSize:10,marginTop:2}}>/ 5.0</div>
                </div>
              </div>
            </div>
          )}
          {tab==='fotolar'&&(
            hasRealPhotos ? (
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,paddingBottom:8}}>
                {PHOTOS.map((ph, i) => (
                  <div key={i} onClick={() => setLightboxIdx(i)}
                    style={{position:'relative',aspectRatio:'1',borderRadius:10,overflow:'hidden',cursor:'pointer',background:'#111'}}
                    onMouseEnter={e => { e.currentTarget.querySelector('.lb-overlay').style.opacity='1'; }}
                    onMouseLeave={e => { e.currentTarget.querySelector('.lb-overlay').style.opacity='0'; }}>
                    <img src={ph.url} alt={ph.label} loading="lazy" decoding="async" onError={onImgErr} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                    <div className="lb-overlay" style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',opacity:0,transition:'opacity 0.15s',fontSize:20}}>
                      🔍
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{textAlign:'center',padding:'40px 0',color:C('txt2')}}>
                <div style={{fontSize:48,marginBottom:12}}>📷</div>
                <p style={{fontSize:13}}>Fotoğraf yüklemek için okul panelini kullanın.</p>
              </div>
            )
          )}
          {tab==='yorumlar'&&(
            <div>
              <ReviewForm place={memoPlace} theme={theme} user={user} color={color} onSubmitted={()=>showToast('✅ Yorumunuz incelemeye alındı')} setShowAuth={onShowAuth} />
              {reviewsLoading && <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,padding:24,color:C('txt2'),fontSize:13}}><Maskot type="mod" name="thinking" size={48}/>Yükleniyor...</div>}
              {!reviewsLoading && reviews.length===0 && <div style={{textAlign:'center',padding:'24px 0',color:C('txt2'),fontSize:13}}><Maskot type="mod" name="sad" size={56} style={{margin:'0 auto 8px'}}/>Henüz yorum yok</div>}
              {reviews.map(r=>(
                <div key={r.id} style={{padding:'12px 0',borderBottom:'1px solid '+C('bord')}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <span style={{color:C('txt'),fontSize:13,fontWeight:700}}>{r.user_name||'Anonim'}</span>
                    <span style={{color:'#FFE66D',fontSize:12}}>{'★'.repeat(r.rating)}</span>
                  </div>
                  <p style={{color:C('txt2'),fontSize:12,lineHeight:1.6,margin:0}}>{r.comment}</p>
                  <p style={{color:C('txt3'),fontSize:10,marginTop:4}}>{new Date(r.created_at).toLocaleDateString('tr-TR')}</p>
                  {r.reply && (
                    <div style={{marginTop:8,padding:'10px 12px',background:'rgba(168,218,255,0.06)',borderLeft:'3px solid rgba(168,218,255,0.45)',borderRadius:'0 8px 8px 0'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                        <span style={{color:'#A8DAFF',fontSize:11,fontWeight:700}}>↳ Okul Yanıtı</span>
                        {r.reply_at && <span style={{color:C('txt3'),fontSize:10}}>· {new Date(r.reply_at).toLocaleDateString('tr-TR')}</span>}
                      </div>
                      <p style={{color:C('txt2'),fontSize:12,lineHeight:1.55,margin:0}}>{r.reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {tab==='genel'&&<>
          {/* Okul başlığı */}
          {(() => { const mk = getMaskotForCategory(p.type); return (
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <Maskot type={mk.type} name={mk.name} size={28}/>
              <div style={{fontSize:11,color:color,fontWeight:600,letterSpacing:'0.05em'}}>{cat.icon||''} {(cat.label||'').toUpperCase()}</div>
            </div>
          ); })()}
          {/* Akıllı eşleştirme rozeti */}
          {(()=>{
            const score = matchScore(p, userPrefs);
            if (score == null) {
              return (
                <div onClick={onOpenPrefs} style={{display:'flex',alignItems:'center',gap:8,padding:'10px 12px',marginBottom:10,background:'rgba(168,218,255,0.06)',border:'1px dashed rgba(168,218,255,0.35)',borderRadius:10,cursor:'pointer'}}>
                  <span style={{fontSize:18}}>🎯</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:'#A8DAFF',fontSize:12,fontWeight:700,marginBottom:1}}>Sana özel uygunluk skoru</div>
                    <div style={{color:C('txt2'),fontSize:10}}>Tercihini söyle, her okula 0-100 rozet alalım</div>
                  </div>
                  <span style={{color:'#A8DAFF',fontSize:14}}>›</span>
                </div>
              );
            }
            const sc = scoreColor(score);
            return (
              <div onClick={onOpenPrefs} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',marginBottom:10,background:sc+'14',border:'1px solid '+sc+'45',borderRadius:10,cursor:'pointer'}}>
                <div style={{width:42,height:42,borderRadius:'50%',background:sc+'25',border:'2px solid '+sc,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{color:sc,fontSize:13,fontWeight:800,fontFamily:'Syne,sans-serif',lineHeight:1}}>%{score}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:sc,fontSize:13,fontWeight:700}}>{score>=85?'Sana çok uygun':score>=70?'Sana uygun':score>=50?'Kısmen uygun':'Az uygun'}</div>
                  <div style={{color:C('txt2'),fontSize:10}}>Tercihini düzenle</div>
                </div>
                <span style={{color:C('txt3'),fontSize:14}}>›</span>
              </div>
            );
          })()}
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
            <span style={{fontSize:20,fontWeight:800,color:C('txt'),fontFamily:'Syne,sans-serif'}}>{p.name}</span>
            {p.is_verified ? (
              <span title="Profil oluşturulmuş & Doğrulanmış Okul" style={{display:'inline-flex',alignItems:'center',gap:3,background:'linear-gradient(135deg,#A8DAFF22,#7BB8F022)',border:'1px solid #A8DAFF44',borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:700,color:'#A8DAFF',flexShrink:0}}>
                ✓ Doğrulandı
              </span>
            ) : (
              <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'rgba(255,230,0,0.1)',border:'1px solid rgba(255,230,0,0.3)',borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:700,color:'#FFE600',flexShrink:0}}>
                ⚠️ Onaylanmamış
              </span>
            )}
          </div>
          {!p.is_verified && (
            <div style={{background:'rgba(255,230,0,0.07)',border:'1px solid rgba(255,230,0,0.25)',borderRadius:10,padding:'8px 12px',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:14,flexShrink:0}}>⚠️</span>
              <span style={{color:'#FFE600',fontSize:12,lineHeight:1.4}}>Bu kurum henüz doğrulanmamıştır. Bilgiler eksik veya hatalı olabilir.</span>
            </div>
          )}
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
            <span style={{color:'#FFE66D',fontWeight:700,fontSize:13}}>★ {p.rating}</span>
            <span style={{color:C('txt2'),fontSize:12}}>({p.reviews} yorum)</span>
            <span style={{color:C('txt3')}}>·</span>
            <span style={{color:p.open?'#4ECDC4':'#888',fontSize:12}}>{p.open?'🟢 Açık':'🔴 Kapalı'}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:2,marginBottom:14}}>
            {[1,2,3,4,5].map(i=>(
              <span key={i} style={{fontSize:18,color:i<=Math.round(p.rating)?'#FFE66D':'rgba(255,255,255,0.12)',lineHeight:1}}>★</span>
            ))}
            <span style={{color:C('txt3'),fontSize:11,marginLeft:6}}>{p.rating}/5.0 · {p.reviews} yorum</span>
          </div>

          {/* Bilgi kartları */}
          <div style={{display:'flex',gap:8,marginBottom:14}}>
            {[
              {val:p.open?'🟢 Açık':'🔴 Kapalı', sub:p.hours,    col:p.open?'#4ECDC4':'#888'},
              {val:p.price,                        sub:'Aidat',     col:p.priceNum>0?color:C('txt3'), weight:p.priceNum>0?700:500},
              {val:p.age+' yaş',                   sub:'Yaş grubu', col:'#A8DAFF'},
            ].map((item,i)=>(
              <div key={i} style={{flex:1,background:C('surf2'),borderRadius:10,padding:10,border:'1px solid '+C('bord'),textAlign:'center'}}>
                <div style={{fontSize:11,fontWeight:item.weight||700,color:item.col,marginBottom:2}}>{item.val}</div>
                <div style={{fontSize:10,color:C('txt2')}}>{item.sub}</div>
              </div>
            ))}
          </div>

          <p style={{color:C('txt2'),fontSize:13,lineHeight:1.7,marginBottom:14}}>{p.desc}</p>
          {p.features.length>0 && (
            <div style={{marginBottom:14}}>
              <div style={{color:C('txt3'),fontSize:11,fontWeight:600,letterSpacing:'0.08em',marginBottom:8}}>ÖZELLİKLER</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {p.features.map((f,i)=>(
                  <span key={i} style={{background:color+'15',border:'1px solid '+color+'30',borderRadius:8,padding:'5px 10px',fontSize:11,color:color,fontWeight:500}}>{f}</span>
                ))}
              </div>
            </div>
          )}
          {schoolBadges.length > 0 && (()=>{
            const displayed = schoolBadges.slice(0,10);
            return (
              <div style={{marginBottom:14}}>
                <div style={{color:C('txt3'),fontSize:11,fontWeight:600,letterSpacing:'0.08em',marginBottom:8}}>🏆 ROZETLER</div>
                {Object.keys(BADGE_CAT_LABELS).map(catKey=>{
                  const catBadges = displayed.filter(sb=>{ const def=BADGES.find(b=>b.key===sb.badge_key); return def&&def.cat===catKey; });
                  if (!catBadges.length) return null;
                  return (
                    <div key={catKey} style={{marginBottom:8}}>
                      <div style={{fontSize:10,color:C('txt3'),fontWeight:600,marginBottom:4}}>{BADGE_CAT_LABELS[catKey]}</div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                        {catBadges.map(sb=>{
                          const def=BADGES.find(b=>b.key===sb.badge_key);
                          if (!def) return null;
                          const isVerified=sb.verified;
                          return (
                            <span key={sb.badge_key} title={isVerified?'Doğrulandı':'Onay Bekliyor'}
                              style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 9px',borderRadius:20,fontSize:11,fontWeight:600,background:isVerified?'rgba(78,205,196,0.12)':'rgba(255,255,255,0.04)',border:'1px solid '+(isVerified?'rgba(78,205,196,0.35)':'rgba(255,255,255,0.08)'),color:isVerified?'#4ECDC4':C('txt3')}}>
                              {def.emoji} {def.label} {isVerified?'✅':'⏳'}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {p.okulbot && (
            <div style={{background:'rgba(168,218,255,0.08)',border:'1px solid rgba(168,218,255,0.2)',borderRadius:12,padding:12,marginBottom:14,display:'flex',alignItems:'center',gap:10}}>
              <img src="/logo.png?v=3" alt="OkulBot" style={{height:32,objectFit:'contain',flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{color:'#A8DAFF',fontSize:12,fontWeight:700}}>OkulBot ile Yönetiliyor</div>
                <div style={{color:C('txt2'),fontSize:11}}>Dijital yönetim paneline geçin</div>
              </div>
              <button onClick={()=>window.open(p.okulbot,'_blank')}
                style={{padding:'6px 10px',borderRadius:8,background:'rgba(168,218,255,0.15)',border:'1px solid rgba(168,218,255,0.3)',color:'#A8DAFF',fontSize:11,fontWeight:700,cursor:'pointer'}}>Panele Geç →</button>
            </div>
          )}
          {/* İletişim bilgileri */}
          {(p.phone || p.email || p.website || p.address) && (
            <div style={{background:C('surf2'),border:'1px solid '+C('bord'),borderRadius:12,overflow:'hidden',marginBottom:14}}>
              <div style={{padding:'8px 14px',borderBottom:'1px solid '+C('bord'),fontSize:10,fontWeight:700,color:C('txt3'),letterSpacing:'0.08em'}}>İLETİŞİM</div>
              {user ? (<>
                {p.phone && (
                  <a href={'tel:'+p.phone} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:(p.email||p.website||p.address)?'1px solid '+C('bord'):'none',textDecoration:'none',color:C('txt')}}>
                    <span style={{fontSize:15,flexShrink:0}}>📞</span>
                    <span style={{fontSize:12,color:'#4ECDC4'}}>{p.phone}</span>
                  </a>
                )}
                {p.email && (
                  <a href={'mailto:'+p.email} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:(p.website||p.address)?'1px solid '+C('bord'):'none',textDecoration:'none',color:C('txt')}}>
                    <span style={{fontSize:15,flexShrink:0}}>📧</span>
                    <span style={{fontSize:12,color:'#A8DAFF'}}>{p.email}</span>
                  </a>
                )}
                {p.website && (
                  <a href={p.website.startsWith('http')?p.website:'https://'+p.website} target="_blank" rel="noopener noreferrer" style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:p.address?'1px solid '+C('bord'):'none',textDecoration:'none',color:C('txt')}}>
                    <span style={{fontSize:15,flexShrink:0}}>🌐</span>
                    <span style={{fontSize:12,color:color,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.website.replace(/^https?:\/\//,'')}</span>
                  </a>
                )}
                {p.address && (
                  <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 14px'}}>
                    <span style={{fontSize:15,flexShrink:0}}>📍</span>
                    <span style={{fontSize:12,color:C('txt2'),lineHeight:1.5}}>{p.address}{p.city?', '+p.city:''}</span>
                  </div>
                )}
              </>) : (
                <div style={{padding:'18px 14px',display:'flex',alignItems:'center',gap:12}}>
                  <span style={{fontSize:22,flexShrink:0}}>🔒</span>
                  <div style={{flex:1}}>
                    <div style={{color:C('txt'),fontSize:13,fontWeight:600,marginBottom:3}}>İletişim bilgilerini görmek için giriş yapın</div>
                    <div style={{color:C('txt2'),fontSize:11}}>Telefon, e-posta ve adres bilgilerine erişin</div>
                  </div>
                  <button onClick={()=>onShowAuth('login')}
                    style={{flexShrink:0,padding:'7px 12px',borderRadius:10,background:color,border:'none',color:'#000',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                    Giriş Yap
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Kayıt Takvimi mini widget */}
          {(()=>{
            const m = new Date().getMonth(); // 0=Ocak
            // 1=Şubat → 2=Mart kayıt başlar; 3=Nisan → 4=Mayıs kayıt biter; 8=Eylül okul başlar
            let status, color, msg, schoolStartHint;
            if (m===1 || m===2) { status='AÇIK'; color='#4ECDC4'; msg='Kayıt dönemi devam ediyor — başvuru yap'; }
            else if (m===3 || m===4) { status='SON GÜNLER'; color='#FFE66D'; msg='Kayıt dönemi yakında kapanır'; }
            else if (m>=5 && m<=7) { status='KAPANDI'; color='#FF8C42'; msg='Bu yılın kayıtları kapandı, eylülde okul başlıyor'; }
            else if (m===8) { status='OKUL BAŞLADI'; color='#A8DAFF'; msg='Eğitim dönemi devam ediyor'; }
            else { status='YAKINDA'; color='#A8DAFF'; msg='Sonraki dönem kayıtları şubatta açılır'; schoolStartHint=true; }
            return (
              <div style={{background:C('surf2'),border:'1px solid '+C('bord'),borderRadius:12,padding:'12px 14px',marginBottom:10}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                  <span style={{fontSize:18}}>📋</span>
                  <div style={{flex:1}}>
                    <div style={{color:C('txt'),fontSize:12,fontWeight:700}}>Kayıt Takvimi</div>
                  </div>
                  <span style={{padding:'3px 8px',borderRadius:6,background:color+'20',border:'1px solid '+color+'55',color:color,fontSize:10,fontWeight:800,letterSpacing:'0.04em'}}>{status}</span>
                </div>
                <div style={{color:C('txt2'),fontSize:11,lineHeight:1.5,marginBottom:8}}>{msg}</div>
                <div style={{display:'flex',gap:4,fontSize:10}}>
                  {[
                    {label:'Şub-Mar', active:m===1||m===2, c:'#4ECDC4'},
                    {label:'Nis-May', active:m===3||m===4, c:'#FFE66D'},
                    {label:'Yaz', active:m>=5&&m<=7, c:'#FF8C42'},
                    {label:'Eylül', active:m===8, c:'#A8DAFF'},
                  ].map(s => (
                    <div key={s.label} style={{flex:1,padding:'4px 0',textAlign:'center',borderRadius:5,background:s.active?s.c+'25':'rgba(255,255,255,0.03)',border:'1px solid '+(s.active?s.c+'55':'transparent'),color:s.active?s.c:C('txt3'),fontWeight:s.active?700:500}}>
                      {s.label}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Kurum profili oluşturma banner */}
          {!p.is_verified && !p.owner_claimed && (
          <div style={{background:'rgba(168,218,255,0.08)',border:'1px solid rgba(168,218,255,0.2)',borderRadius:12,padding:'14px 16px',marginBottom:8,display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:28,flexShrink:0}}>🏫</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:'#A8DAFF',fontSize:13,fontWeight:700,marginBottom:2}}>Bu kuruma yetkili misiniz?</div>
              <div style={{color:C('txt2'),fontSize:11,lineHeight:1.4}}>Profil oluşturun →</div>
            </div>
            <button onClick={onClaim}
              style={{flexShrink:0,padding:'8px 12px',borderRadius:10,background:color,border:'none',color:'#000',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',whiteSpace:'nowrap'}}>
              Talep Gönder
            </button>
          </div>
          )}
          </>}
        </div>

        {/* Actions */}
        <div style={{padding:'12px 18px 16px',borderTop:'1px solid '+C('bord'),background:C('surf'),flexShrink:0}}>
          <div style={{display:'flex',gap:8,marginBottom:8}}>
            <button onClick={onBilgiAl}
              style={{flex:1,padding:13,borderRadius:12,background:'linear-gradient(135deg,'+color+','+color+'bb)',border:'none',color:'#000',fontSize:13,fontWeight:700,cursor:'pointer',boxShadow:'0 4px 16px '+color+'40'}}>
              📋 Bilgi Al
            </button>
            <button onClick={onWalkRoute}
              style={{flex:1,padding:13,borderRadius:12,background:'rgba(168,218,255,0.12)',border:'1px solid rgba(168,218,255,0.3)',color:'#A8DAFF',fontSize:13,fontWeight:700,cursor:'pointer'}}>
              🚶 Yürüyerek Git
            </button>
            {p.phone && p.owner_claimed && planAtLeast(schoolPlan, 'premium') && (
              <a href={'https://wa.me/'+p.phone.replace(/\D/g,'')+'?text='+encodeURIComponent('Merhaba, OkulMap\'ten yazıyorum. '+p.name+' hakkında bilgi almak istiyorum.')} target="_blank" rel="noopener noreferrer"
                title="WhatsApp ile iletişim"
                style={{width:46,height:46,borderRadius:12,background:'rgba(37,211,102,0.14)',border:'1px solid rgba(37,211,102,0.4)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',flexShrink:0,textDecoration:'none'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
              </a>
            )}
            <button onClick={onShare} style={{width:46,height:46,borderRadius:12,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',flexShrink:0}}>
              <Share01Icon size={18} color='rgba(255,255,255,0.7)' />
            </button>
          </div>
          {walkRoute && walkRoute.schoolId === place.id && (
            <div style={{display:'flex',flexDirection:'column',gap:6,background:'rgba(168,218,255,0.08)',borderRadius:10,padding:'10px 12px',border:'1px solid rgba(168,218,255,0.2)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
                {walkRoute.duration != null && (
                  <span style={{color:'#A8DAFF',fontSize:13,fontWeight:700}}>🚶 {walkRoute.duration} dk · {walkRoute.distance}</span>
                )}
                {walkRoute.driveDuration != null && (
                  <span style={{color:'#FFE66D',fontSize:13,fontWeight:700}}>🚗 sabah 08:00 trafiğinde {walkRoute.driveDuration} dk · {walkRoute.driveDistance}</span>
                )}
                <button onClick={onClearWalkRoute} style={{background:'none',border:'none',color:'rgba(168,218,255,0.5)',fontSize:11,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginLeft:'auto'}}>Rotayı Temizle ✕</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div style={{position:'fixed',inset:0,zIndex:900,background:'rgba(0,0,0,0.92)',display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={() => setLightboxIdx(null)}>
          <img
            src={PHOTOS[lightboxIdx].url}
            alt={PHOTOS[lightboxIdx].label}
            loading="eager"
            decoding="async"
            onError={onImgErr}
            onClick={e => e.stopPropagation()}
            style={{maxWidth:'90vw',maxHeight:'90vh',objectFit:'contain',borderRadius:8,boxShadow:'0 24px 80px rgba(0,0,0,0.6)'}}
          />
          {/* Kapat */}
          <button onClick={() => setLightboxIdx(null)}
            style={{position:'fixed',top:20,right:20,width:40,height:40,borderRadius:12,background:'rgba(255,255,255,0.12)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff',fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:901}}>
            ✕
          </button>
          {/* Sol ok */}
          {PHOTOS.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + PHOTOS.length) % PHOTOS.length); }}
              style={{position:'fixed',left:16,top:'50%',transform:'translateY(-50%)',width:44,height:44,borderRadius:12,background:'rgba(255,255,255,0.12)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff',fontSize:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:901}}>
              ‹
            </button>
          )}
          {/* Sağ ok */}
          {PHOTOS.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % PHOTOS.length); }}
              style={{position:'fixed',right:16,top:'50%',transform:'translateY(-50%)',width:44,height:44,borderRadius:12,background:'rgba(255,255,255,0.12)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff',fontSize:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:901}}>
            ›
          </button>
          )}
          {/* Sayaç */}
          <div style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',background:'rgba(0,0,0,0.6)',borderRadius:20,padding:'4px 14px',color:'rgba(255,255,255,0.7)',fontSize:12,fontFamily:'DM Sans,sans-serif'}}>
            {lightboxIdx + 1} / {PHOTOS.length}
          </div>
        </div>
      )}
    </div>
  );
});
